<template>
  <div class="netx-card">
    <div class="netx-thumb">
      <v-img v-if="assetImageUrl && !imageFailed" :src="assetImageUrl" height="150" contain class="grey lighten-3" @error="imageFailed = true">
        <v-layout slot="placeholder" fill-height align-center justify-center ma-0>
          <v-progress-circular indeterminate color="grey darken-1" />
        </v-layout>
      </v-img>
      <div v-else class="netx-image-placeholder">
        <v-icon color="grey darken-1">broken_image</v-icon>
        <span>{{ assetImageUrl ? 'Preview unavailable' : 'Preview not ready' }}</span>
      </div>
    </div>
    <div class="netx-body">
      <div class="netx-name" :title="displayName">{{ displayName }}</div>
      <div class="netx-line" v-if="asset.accession_number">{{ asset.accession_number }}</div>
      <div class="netx-line" v-if="asset.width || asset.height">
        {{ asset.width || '?' }} × {{ asset.height || '?' }}
      </div>
      <div class="netx-flags" v-if="asset.ftb_favorite || asset.ftb_approved || asset.publication || asset.website">
        <span v-if="asset.ftb_favorite">FTB favorite</span>
        <span v-if="asset.ftb_approved">FTB approved</span>
        <span v-if="asset.publication">Publication</span>
        <span v-if="asset.website">Website</span>
      </div>
      <div class="netx-tags" v-if="asset.tags && asset.tags.length">
        <v-chip v-for="tag in asset.tags.slice(0, 3)" :key="tag.id || tag.name" small label outline>
          {{ tag.name }}
        </v-chip>
      </div>
      <div class="netx-credit" v-if="creditLine">{{ creditLine }}</div>
      <div class="netx-unavailable" v-if="!asset.promotable">{{ asset.promotion_block_reason }}</div>
      <div class="netx-quality-warning" v-if="asset.public_website_quality_warning">
        <v-icon small>warning</v-icon>
        <span>{{ asset.public_website_quality_message }}</span>
      </div>
      <div class="netx-local-warning" v-if="asset.artwork_photo_metadata_differs">
        <v-icon small>info</v-icon>
        <span>CMS image information differs from NetX.</span>
      </div>
      <div class="netx-media-state" :class="`is-${mediaStatusValue}`">
        <v-progress-circular v-if="mediaIsActive" indeterminate :size="13" :width="2" color="inherit" />
        <v-icon v-else-if="mediaStatusValue === 'ready'" small>check_circle</v-icon>
        <v-icon v-else-if="mediaStatusValue === 'failed' || mediaStatusValue === 'partial'" small>error</v-icon>
        <span>{{ mediaStatusLabelValue }}</span>
      </div>
      <div v-if="mediaErrors.length" class="netx-media-errors">
        <div v-for="message in mediaErrors" :key="message">{{ message }}</div>
        <v-btn v-if="canRetry" small flat class="px-0" :loading="retrying" @click="retryMedia">
          <v-icon small left>refresh</v-icon> Retry failed versions
        </v-btn>
      </div>
    </div>
    <div class="netx-footer">
      <label v-if="context !== 'public'" class="netx-control" :class="{ 'is-disabled': importDisabled }">
        <input type="checkbox" :checked="selectionValue('imported')" :disabled="importDisabled" @change="requestSelection({ imported: true })" />
        <span>Imported</span>
      </label>
      <label class="netx-control" :class="{ 'is-disabled': selectionDisabled }">
        <input type="checkbox" :checked="selectionValue('public_site')" :disabled="selectionDisabled" @change="requestPublicSite" />
        <span>Public website</span>
      </label>
      <label v-if="context !== 'public'" class="netx-control" :class="{ 'is-disabled': selectionDisabled }">
        <input type="radio" :name="`netx-cms-top-${artwork.id}`" :checked="selectionValue('cms_top')" :disabled="selectionDisabled" @change="requestSelection({ cms_top: true })" />
        <span>CMS top</span>
      </label>
      <label class="netx-control" :class="{ 'is-disabled': selectionDisabled }">
        <input type="radio" :name="`netx-public-top-${artwork.id}`" :checked="selectionValue('public_top')" :disabled="selectionDisabled" @change="requestPublicTop" />
        <span>Public top</span>
      </label>
      <v-btn
        v-if="isAdmin && artworkPhotoId"
        small
        flat
        class="netx-edit-button"
        @click="$emit('edit', { photoId: artworkPhotoId, asset: asset })"
      >
        <v-icon small left>edit</v-icon>Edit details
      </v-btn>
      <div class="netx-status" :class="`is-${selectionStatus}`">
        <v-progress-circular v-if="selectionActive" indeterminate :size="14" :width="2" color="grey darken-2" />
        <v-icon v-else-if="selectionStatus === 'succeeded'" small>check_circle</v-icon>
        <v-icon v-else-if="selectionStatus === 'failed'" small>error</v-icon>
        <span v-if="selectionActive">Updating</span>
        <span v-else-if="selectionStatus === 'succeeded'">Updated</span>
        <span v-else-if="selectionStatus === 'partial'">Partially updated</span>
        <span v-else-if="selectionStatus === 'failed'">{{ selectionError }}</span>
        <v-btn v-if="canRetryImport" small flat class="px-0" @click="retryImport">
          <v-icon small left>refresh</v-icon> Retry import
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import {
  canRetryMedia,
  failedMediaMessages,
  importedArtworkPhotoId,
  mediaActive,
  mediaReady,
  mediaStatus,
  mediaStatusLabel,
  normalizeMediaUrl,
  retryArtworkPhoto,
  thumbnailUrl
} from '@/utils/artworkMedia'
import { canManageNetx } from '@/utils/netxArtworkReview'

export default {
  props: {
    artwork: { type: Object, required: true },
    asset: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    context: { type: String, default: 'images' }
  },
  data: () => ({ imageFailed: false, retrying: false }),
  watch: {
    assetImageUrl () { this.imageFailed = false }
  },
  computed: {
    selectionStatus () {
      return (this.asset.selection && this.asset.selection.status) || 'idle'
    },
    selectionActive () {
      return !!(this.asset.selection && this.asset.selection.active)
    },
    selectionError () {
      return (this.asset.selection && this.asset.selection.error) || 'Update failed'
    },
    selectionDisabled () {
      return this.disabled || !this.asset.promotable || !this.canManageNetx
    },
    importDisabled () {
      return this.selectionDisabled || this.selectionValue('imported')
    },
    assetImageUrl () {
      return normalizeMediaUrl(thumbnailUrl(this.asset), this.$globals.apiserver)
    },
    mediaStatusValue () {
      const status = mediaStatus(this.asset)
      return ['succeeded', 'complete', 'completed'].includes(status) ? 'ready' : status
    },
    mediaStatusLabelValue () {
      return mediaStatusLabel(this.asset)
    },
    mediaIsActive () {
      return mediaActive(this.asset)
    },
    mediaErrors () {
      return failedMediaMessages(this.asset)
    },
    mediaReady () {
      return mediaReady(this.asset)
    },
    artworkPhotoId () {
      return importedArtworkPhotoId(this.asset)
    },
    canManageNetx () {
      return canManageNetx(this.c_user)
    },
    canRetry () {
      return !!this.artworkPhotoId && canRetryMedia(this.c_user) && ['failed', 'partial'].includes(this.mediaStatusValue)
    },
    isAdmin () {
      const roles = (this.c_user && this.c_user.roles) || []
      return !!(this.c_user && (this.c_user.admin === true || Number(this.c_user.admin) === 1)) ||
        roles.some(role => ['admin', 'superadmin', 'root'].includes(String(role.shortcut || role.role || '').toLowerCase()))
    },
    canRetryImport () {
      return this.canManageNetx && this.selectionStatus === 'failed' && !this.artworkPhotoId && !this.selectionDisabled
    },
    creditLine () {
      if (!this.asset.credits || !this.asset.credits.length) return ''
      return this.asset.credits.slice(0, 2).map(credit => credit.name || credit.role).filter(Boolean).join(', ')
    },
    displayName () {
      return this.asset.title || this.asset.file_name || 'NetX ' + this.asset.netx_id
    }
  },
  methods: {
    selectionValue (key) {
      const selection = this.asset.selection || {}
      if (selection.active && selection.desired) return !!selection.desired[key]
      return !!selection[key]
    },
    requestSelection (changes) {
      if (this.selectionDisabled) return
      const desired = Object.assign({
        imported: this.selectionValue('imported'),
        public_site: this.selectionValue('public_site'),
        cms_top: this.selectionValue('cms_top'),
        public_top: this.selectionValue('public_top')
      }, changes)
      if (desired.public_site || desired.cms_top || desired.public_top) desired.imported = true
      if (desired.public_top) desired.public_site = true
      this.$emit('select', { asset: this.asset, desired })
    },
    requestPublicSite (event) {
      const selected = event.target.checked
      if (selected && this.selectionValue('imported') && !this.mediaReady) {
        event.target.checked = this.selectionValue('public_site')
        this.$emit('notice', 'Public selection is unavailable until image processing completes.')
        return
      }
      if (selected && !this.confirmPublicQuality()) {
        event.target.checked = this.selectionValue('public_site')
        return
      }
      this.requestSelection({
        public_site: selected,
        public_top: selected ? this.selectionValue('public_top') : false
      })
    },
    requestPublicTop (event) {
      if (this.selectionValue('imported') && !this.mediaReady) {
        event.target.checked = this.selectionValue('public_top')
        this.$emit('notice', 'Public selection is unavailable until image processing completes.')
        return
      }
      if (!this.confirmPublicQuality()) {
        event.target.checked = this.selectionValue('public_top')
        return
      }
      this.requestSelection({ public_top: true, public_site: true })
    },
    confirmPublicQuality () {
      if (!this.asset.public_website_quality_warning) return true
      return window.confirm(`${this.asset.public_website_quality_message}\n\nUse this image on the public website anyway?`)
    },
    retryMedia () {
      if (!this.canRetry || this.retrying) return
      this.retrying = true
      retryArtworkPhoto(this.artworkPhotoId)
        .then(response => this.$emit('retry', response.data))
        .catch(error => this.$emit('notice', (error.response && error.response.data && error.response.data.error) || 'Image processing could not be retried.'))
        .finally(() => { this.retrying = false })
    },
    retryImport () {
      if (!this.canRetryImport) return
      const selection = this.asset.selection || {}
      this.requestSelection(Object.assign({}, selection.desired || {}, { imported: true }))
    }
  }
}
</script>

<style scoped>
.netx-card { background: #fff; border: 1px solid #deded9; display: flex; flex-direction: column; min-height: 390px; }
.netx-thumb { border-bottom: 1px solid #e2e2dc; }
.netx-image-placeholder { align-items: center; background: #ecece8; color: #777; display: flex; flex-direction: column; height: 150px; justify-content: center; gap: 5px; }
.netx-image-placeholder span { font-size: 11px; }
.netx-body { flex: 1 1 auto; padding: 10px; }
.netx-name { color: #141414; font-size: 13px; font-weight: 700; line-height: 1.3; min-height: 34px; overflow: hidden; }
.netx-line { color: #555; font-size: 12px; margin-top: 4px; }
.netx-flags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
.netx-flags span { background: #ecece7; border-left: 2px solid #111; color: #333; font-size: 10px; font-weight: 700; padding: 3px 5px; text-transform: uppercase; }
.netx-tags { margin-top: 8px; min-height: 30px; }
.netx-credit { color: #6b6258; font-size: 12px; line-height: 1.3; margin-top: 6px; }
.netx-unavailable { color: #a32929; font-size: 12px; line-height: 1.3; margin-top: 8px; }
.netx-quality-warning { align-items: flex-start; color: #8a4b00; display: flex; font-size: 12px; gap: 5px; line-height: 1.3; margin-top: 8px; }
.netx-quality-warning .v-icon { color: inherit; flex: 0 0 auto; }
.netx-local-warning { align-items: flex-start; color: #8a4b00; display: flex; font-size: 12px; gap: 5px; line-height: 1.3; margin-top: 8px; }
.netx-local-warning .v-icon { color: inherit; flex: 0 0 auto; }
.netx-media-state { align-items: center; display: flex; font-size: 11px; gap: 5px; margin-top: 8px; }
.netx-media-state.is-ready { color: #276638; }
.netx-media-state.is-failed, .netx-media-state.is-partial { color: #a32929; }
.netx-media-errors { color: #a32929; font-size: 11px; line-height: 1.3; margin-top: 4px; }
.netx-media-errors .v-btn { color: inherit; font-size: 11px; min-width: 0; }
.netx-footer { border-top: 1px solid #e2e2dc; display: grid; gap: 6px 12px; grid-template-columns: 1fr 1fr; min-height: 92px; padding: 9px 10px; }
.netx-control { align-items: center; color: #222; cursor: pointer; display: flex; font-size: 12px; gap: 7px; min-height: 22px; }
.netx-control input { height: 15px; margin: 0; width: 15px; }
.netx-control.is-disabled { color: #999; cursor: default; }
.netx-edit-button { align-self: center; grid-column: 1 / -1; justify-self: start; margin: 0; min-width: 0; padding: 0 6px; }
.netx-status { align-items: center; color: #555; display: flex; font-size: 11px; gap: 5px; grid-column: 1 / -1; min-height: 18px; }
.netx-status.is-succeeded { color: #276638; }
.netx-status.is-failed { color: #a32929; }
.netx-status .v-icon { color: inherit; }
.netx-status .v-btn { color: inherit; font-size: 11px; min-width: 0; margin: 0 0 0 4px; }
</style>



// WEBPACK FOOTER //
// src/components/artworks/NetxAssetCard.vue